import {
  UtensilsCrossed, Coffee, Cake, Wine, ShoppingBag, Store,
  Scissors, Dumbbell, Flower2, Dog, ShoppingCart, IceCream,
  Armchair, BookOpen, Pill, Tent, Heart, Sparkles, Beer, Salad, Hotel, ChefHat
} from "lucide-react";
import { getCityProfile, getCityCurrency } from "./marketData";
import { getCityPermits, getCountryGenericPermits } from "./cityPermitData";
import { formatAreaRange, areaUnit, convertArea } from "./areaUnit";
import { getCityData } from "./locationData";
import { getCityMultiplier } from "./cityMultipliers";
import { getBudgetOptionsForCity } from "./currency";
import {
  BUSINESS_TYPE_PROFILES,
  getBusinessTypeProfile,
} from "./businessTypeProfiles";
import { calculateRestaurantStaffingDefaults, isRestaurantStaffingConcept } from "./staffingRatios";

export const businessTypes = [
  { id: "restaurant", label: "Restaurant", icon: UtensilsCrossed },
  { id: "coffee-shop", label: "Coffee Shop", icon: Coffee },
  { id: "bakery", label: "Bakery", icon: Cake },
  { id: "bar", label: "Bar", icon: Beer },
  { id: "bakery-cafe", label: "Bakery Café", icon: Salad },
  { id: "pizzeria", label: "Pizzeria", icon: UtensilsCrossed },
  { id: "juice-bar", label: "Juice / Smoothie Bar", icon: Coffee },
  { id: "ice-cream-shop", label: "Ice Cream Shop", icon: IceCream },
  { id: "wine-shop", label: "Wine Bar / Wine Shop", icon: Wine },
  { id: "food-truck", label: "Food Truck", icon: Store },
  { id: "fast-casual", label: "Fast Casual", icon: ShoppingBag },
  { id: "brunch-spot", label: "Brunch Spot", icon: UtensilsCrossed },
  { id: "retail-boutique", label: "Retail Boutique", icon: ShoppingBag },
  { id: "convenience-store", label: "Convenience Store", icon: Store },
  { id: "beauty-salon", label: "Beauty Salon", icon: Sparkles },
  { id: "barber-shop", label: "Barber Shop", icon: Scissors },
  { id: "nail-salon", label: "Nail Salon", icon: Heart },
  { id: "fitness-studio", label: "Fitness Studio", icon: Dumbbell },
  { id: "flower-shop", label: "Flower Shop", icon: Flower2 },
  { id: "pet-store", label: "Pet Store", icon: Dog },
  { id: "grocery-store", label: "Grocery Store", icon: ShoppingCart },
  { id: "furniture-store", label: "Furniture Store", icon: Armchair },
  { id: "bookstore", label: "Bookstore", icon: BookOpen },
  { id: "pharmacy", label: "Pharmacy", icon: Pill },
  { id: "pop-up-shop", label: "Pop-up Shop", icon: Tent },
  { id: "hotel", label: "Hotel & Lodging", icon: Hotel },
  // Types defined in onboarding's bespoke flow that also need to resolve to a
  // label here (used by the Opening Plan summary, PDF, etc.).
  { id: "ghost-kitchen", label: "Virtual Kitchen", icon: ChefHat },
  { id: "bed-and-breakfast", label: "Bed & Breakfast", icon: Hotel },
  { id: "private-events", label: "Private Events & Catering", icon: UtensilsCrossed },
  { id: "private-chef", label: "Private Chef", icon: ChefHat },
];

export const budgetRanges = [
  { id: "under-50k", label: "Under $50K", value: 50000 },
  { id: "50k-100k", label: "$50K – $100K", value: 100000 },
  { id: "100k-250k", label: "$100K – $250K", value: 250000 },
  { id: "250k-500k", label: "$250K – $500K", value: 500000 },
  { id: "500k-plus", label: "$500K+", value: 750000 },
];

export const experienceLevels = [
  { id: "none", label: "Complete beginner", description: "I've never opened a business" },
  { id: "some", label: "Some experience", description: "I've worked in this industry" },
  { id: "experienced", label: "Experienced", description: "I've opened a business before" },
];

export const businessSizes = [
  { id: "small", label: "Small", description: "Cozy, intimate, low overhead" },
  { id: "medium", label: "Medium", description: "Standard size, moderate capacity" },
  { id: "flagship", label: "Flagship", description: "Large, high-capacity, premium" },
];

export const locationTypes = [
  { id: "permanent", label: "Permanent location" },
  { id: "kiosk", label: "Kiosk" },
  { id: "pop-up", label: "Pop-up" },
  { id: "home-based", label: "Home-based" },
];

export const launchSpeeds = [
  { id: "fast", label: "Fast launch", description: "Open as quickly as possible" },
  { id: "balanced", label: "Balanced", description: "Good location, reasonable timeline" },
  { id: "best-location", label: "Best location", description: "Take time to find the perfect spot" },
];

export const serviceStyles = [
  { id: "counter", label: "Counter service", description: "Customers order at a counter" },
  { id: "full-service", label: "Full service", description: "Table service with waitstaff" },
  { id: "fast-casual", label: "Fast casual", description: "Order at counter, food brought to table" },
  { id: "self-service", label: "Self-service", description: "Buffet, grab-and-go, or self-checkout" },
  { id: "appointment", label: "By appointment", description: "Scheduled visits only" },
];

export const seatingOptions = [
  { id: "indoor", label: "Indoor only" },
  { id: "outdoor", label: "Outdoor only" },
  { id: "both", label: "Indoor + Outdoor" },
];

export const teamSizes = [
  { id: "lean", label: "Lean team", description: "Just you + 1–3 people. Keep costs low at first." },
  { id: "full", label: "Full team", description: "Hire all roles from day one. Higher cost, smoother ops." },
];

export interface OnboardingData {
  city: string;
  businessType: string;
  businessName: string;
  goal: "opening" | "managing" | "";
  selectedPlan: string;
  budget: string;
  hasPartners: boolean;
  experience: string;
  businessSize: string;
  locationType: string;
  targetCustomer: string;
  launchSpeed: string;
  // New fields
  targetOpeningDate: string;
  sqftMin: number;
  sqftMax: number;
  seats: number;
  seatingType: string;
  serviceStyle: string;
  openingHours: string;
  daysPerWeek: number;
  teamSize: string;
  alcoholType: "none" | "beer-wine" | "full-liquor" | string;
  spacePreference: string;
  // Hotel F&B / service outlets — used for hotels to capture in-house outlets
  // (Restaurant, Bar, Spa, etc.) with their own seating capacity. Total covers
  // across outlets feed staffing and permit generation.
  outlets: { name: string; type: string; seats?: number }[];
  // Detailed seating breakdown
  indoorSeats: number;
  outdoorSeats: number;
  barSeats: number;
  // Hotel / B&B room configuration. When present, the venue is room-driven
  // (rather than seat-driven). ADR is stored in USD baseline; occupancy is a
  // percentage 0-100. These feed room-night revenue: rooms × ADR × occ × 30.
  guestRooms?: { type: string; quantity: number; adr: number; occupancy: number }[];
  // Hotel — extra capacity inputs collected in onboarding. `totalRoomsOverride`
  // is only used if the user skipped the room-types builder.
  suitesCount?: number;
  meetingRoomsCapacity?: number;
  // Private chef — event-driven model.
  guestsPerEvent?: number;
  eventsPerMonth?: number;
  kitchenLocation?: "commissary" | "client-home" | "both" | "";
  // Catering / Private Events — event-driven model with a max capacity.
  maxCapacityPerEvent?: number;
  // Food truck — counter seats are optional; daily covers are the main lever.
  counterSeats?: number;
  avgDailyCovers?: number;
  // Managing-an-existing-business onboarding extras. Captured only when
  // goal === "managing"; ignored by the opening flow.
  monthsOpen?: "0-6" | "6-12" | "1-2" | "2+" | "";
  monthlyRevenueEstimate?: number;
  biggestChallenge?:
    | "labor"
    | "food"
    | "revenue"
    | "retention"
    | "compliance"
    | "lease"
    | "operations"
    | "inspection"
    | "";
  country?: string;
}

export interface TimelineSubstep {
  task: string;
  detail: string;
}

export interface TimelineStep {
  step: string;
  duration: string;
  done: boolean;
  substeps: TimelineSubstep[];
  weekStart: number;
  weekEnd: number;
}

export interface PermitDetail {
  name: string;
  simple: string;
  required: boolean;
  link: string;
  processingTime: string;
  estimatedCost: string;
  requirements: string[];
}

export interface ChecklistItem {
  id: string;
  text: string;
  done: boolean;
  sources: { name: string; url: string; }[];
  tip: string;
}

export interface LeaseLocation {
  id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  sqft: number;
  pricePerMonth: number;
  type: string;
  available: boolean;
  features: string[];
}

export interface StaffingRole {
  role: string;
  count: number;
  hourlyRate: string;
  monthlyEstimate: number;
  priority: string;
  note: string;
}

export interface MilestoneItem {
  label: string;
  weeksBeforeOpen: number;
  critical: boolean;
}

// City coordinates for map centering
const cityCoords: Record<string, [number, number]> = {
  "miami": [25.7617, -80.1918],
  "new york": [40.7128, -74.0060],
  "los angeles": [34.0522, -118.2437],
  "chicago": [41.8781, -87.6298],
  "austin": [30.2672, -97.7431],
  "san francisco": [37.7749, -122.4194],
  "seattle": [47.6062, -122.3321],
  "denver": [39.7392, -104.9903],
  "atlanta": [33.7490, -84.3880],
  "dallas": [32.7767, -96.7970],
  "boston": [42.3601, -71.0589],
  "nashville": [36.1627, -86.7816],
  "portland": [45.5152, -122.6784],
  "phoenix": [33.4484, -112.0740],
  "houston": [29.7604, -95.3698],
  "london": [51.5074, -0.1278],
  "manchester": [53.4808, -2.2426],
  "edinburgh": [55.9533, -3.1883],
};

function getCityCenter(city: string): [number, number] {
  const key = city.toLowerCase().trim();
  return cityCoords[key] || [40.7128, -74.0060];
}

function generateLeaseListings(city: string, budgetValue: number): LeaseLocation[] {
  const [lat, lng] = getCityCenter(city);
  return [
    { id: "l1", name: "Main Street Storefront", address: "123 Main St", lat: lat + 0.005, lng: lng + 0.003, sqft: 1200, pricePerMonth: Math.round(budgetValue * 0.02), type: "Retail / Restaurant", available: true, features: ["Street-level", "High foot traffic", "Renovated", "ADA compliant"] },
    { id: "l2", name: "Arts District Space", address: "456 Gallery Ave", lat: lat - 0.008, lng: lng + 0.012, sqft: 900, pricePerMonth: Math.round(budgetValue * 0.013), type: "Retail / Café", available: true, features: ["Corner unit", "Outdoor patio", "New HVAC", "Near transit"] },
    { id: "l3", name: "University Plaza Unit", address: "789 College Blvd", lat: lat + 0.015, lng: lng - 0.008, sqft: 800, pricePerMonth: Math.round(budgetValue * 0.009), type: "Retail / Food Service", available: true, features: ["Near campus", "Parking lot", "Drive-thru possible", "Affordable"] },
    { id: "l4", name: "Downtown Tower Ground Floor", address: "100 Financial Dr", lat: lat + 0.002, lng: lng - 0.004, sqft: 2000, pricePerMonth: Math.round(budgetValue * 0.04), type: "Restaurant / Premium Retail", available: true, features: ["Premium location", "Floor-to-ceiling glass", "Valet", "2 entrances"] },
    { id: "l5", name: "Strip Mall End Cap", address: "2200 Suburban Way", lat: lat - 0.02, lng: lng - 0.015, sqft: 1500, pricePerMonth: Math.round(budgetValue * 0.007), type: "Any Commercial", available: true, features: ["Ample parking", "Drive-thru ready", "Newly built", "Low rent"] },
    { id: "l6", name: "Historic Market Building", address: "55 Market Square", lat: lat + 0.009, lng: lng + 0.007, sqft: 1100, pricePerMonth: Math.round(budgetValue * 0.018), type: "Food & Beverage", available: false, features: ["Character building", "Community anchor", "Shared courtyard", "Events space"] },
  ];
}

function getRecommendedSqft(businessType: string, seats: number, city: string = "miami"): { min: number; max: number; note: string } {
  const sqftPerSeat: Record<string, number> = {
    "restaurant": 18, "coffee-shop": 15, "bakery": 12, "bar": 20,
    "bakery-cafe": 15, "ice-cream-shop": 12, "wine-shop": 14,
  };
  const u = areaUnit(city);
  const perSeat = sqftPerSeat[businessType];
  if (perSeat && seats > 0) {
    const base = seats * perSeat;
    const displayPerSeat = convertArea(perSeat, city);
    return { min: Math.round(base * 0.9), max: Math.round(base * 1.3), note: `Based on ~${displayPerSeat} ${u} per seat for a ${businessType.replace("-", " ")}.` };
  }
  const defaults: Record<string, [number, number]> = {
    "beauty-salon": [600, 1200], "barber-shop": [400, 800], "nail-salon": [500, 1000],
    "fitness-studio": [1500, 3000], "retail-boutique": [800, 1500], "convenience-store": [1000, 2500],
    "flower-shop": [500, 1000], "pet-store": [1200, 2500], "grocery-store": [2000, 5000],
    "furniture-store": [2000, 5000], "bookstore": [800, 2000], "pharmacy": [1000, 2500],
    "pop-up-shop": [200, 600], "hotel": [5000, 50000],
  };
  // Per-business-type sqft from the central profile takes priority for any
  // type that isn't seat-driven (food truck, virtual kitchen, private chef…).
  const profile = getBusinessTypeProfile(businessType);
  const profileSqft: [number, number] | undefined = profile
    ? [profile.sqftRange.min, profile.sqftRange.max]
    : undefined;
  const d = defaults[businessType] || profileSqft || [800, 1500];
  return { min: d[0], max: d[1], note: `Typical range for a ${businessType.replace(/-/g, " ")}.` };
}

function generateStaffing(data: OnboardingData, _budgetValue: number): StaffingRole[] {
  const lean = data.teamSize === "lean";
  // Seats scale linearly with the sqft the user enters — no upper cap so
  // very large venues (e.g. 50,000 sqft) get accurate headcount estimates.
  const seats = Math.max(0, Number(data.seats) || 30);
  const bt = data.businessType;

  // Restaurant
  if (isRestaurantStaffingConcept(bt)) {
    const defaults = calculateRestaurantStaffingDefaults(seats);
    return [
      { role: "General Manager", count: defaults.generalManagers, hourlyRate: "$22–$32", monthlyEstimate: 4800 * defaults.generalManagers, priority: "essential", note: "Always includes one GM to run daily operations." },
      { role: "Kitchen Manager / Chef", count: defaults.kitchenManagers, hourlyRate: "$20–$30", monthlyEstimate: 4200 * defaults.kitchenManagers, priority: "essential", note: "One kitchen lead below 100 seats; two for larger kitchens." },
      { role: "Line Cook", count: defaults.lineCooks, hourlyRate: "$15–$20", monthlyEstimate: 2900 * defaults.lineCooks, priority: "essential", note: "Sized at roughly 1 per 20–25 seats, capped at 8." },
      { role: "Prep Cook", count: defaults.prepCooks, hourlyRate: "$13–$17", monthlyEstimate: 2400 * defaults.prepCooks, priority: "recommended", note: "Approximately 75% of line-cook staffing." },
      { role: "Dishwasher", count: defaults.dishwashers, hourlyRate: "$12–$15", monthlyEstimate: 2000 * defaults.dishwashers, priority: "essential", note: "One per ~40 seats, minimum 1." },
      { role: "Server", count: defaults.servers, hourlyRate: "$3–$8 + tips", monthlyEstimate: 1800 * defaults.servers, priority: "essential", note: `Sized at roughly 1 per 15–20 seats, capped at 12.` },
      { role: "Host", count: defaults.hosts, hourlyRate: "$12–$16", monthlyEstimate: 2200 * defaults.hosts, priority: "recommended", note: "One host below 80 seats; two at 80+ seats." },
    ].filter(r => r.count > 0);
  }

  // Bar & Lounge
  if (bt === "bar" || bt === "wine-shop") {
    const bartenders = Math.min(lean ? 1 : Math.max(2, Math.floor(seats / 15)), 8);
    return [
      { role: "Head Bartender", count: 1, hourlyRate: "$16–$25 + tips", monthlyEstimate: 3600, priority: "essential", note: "Creates the drink menu and trains bar staff." },
      { role: "Bartender", count: bartenders, hourlyRate: "$12–$20 + tips", monthlyEstimate: 2800 * bartenders, priority: "essential", note: "1 per 12–15 seats at the bar. Tips are major income." },
      { role: "Barback", count: lean ? 0 : 1, hourlyRate: "$11–$15", monthlyEstimate: lean ? 0 : 2000, priority: lean ? "optional" : "essential", note: "Restocks ice, glasses, bottles during busy hours." },
      { role: "Bouncer / Door Staff", count: lean ? 0 : 1, hourlyRate: "$14–$20", monthlyEstimate: lean ? 0 : 2600, priority: lean ? "optional" : "recommended", note: "Required if open late. Check local regulations." },
      { role: "Server", count: lean ? 1 : 2, hourlyRate: "$5–$10 + tips", monthlyEstimate: lean ? 1600 : 3200, priority: "essential", note: "Covers table/booth service if you have seating areas." },
      { role: "Bar Manager", count: lean ? 0 : 1, hourlyRate: "$20–$28", monthlyEstimate: lean ? 0 : 4200, priority: lean ? "optional" : "recommended", note: "Manages inventory, ordering, and scheduling." },
    ].filter(r => r.count > 0);
  }

  // Coffee Shop
  if (bt === "coffee-shop") {
    return [
      { role: "Lead Barista", count: 1, hourlyRate: "$15–$20", monthlyEstimate: 3000, priority: "essential", note: "Trains the team and maintains drink quality." },
      { role: "Barista", count: lean ? 1 : 3, hourlyRate: "$12–$17 + tips", monthlyEstimate: lean ? 2200 : 6600, priority: "essential", note: "2 per shift during peak hours. 1 is minimum." },
      { role: "Cashier / Counter Staff", count: lean ? 0 : 1, hourlyRate: "$12–$15", monthlyEstimate: lean ? 0 : 2000, priority: lean ? "optional" : "recommended", note: "Handles register so baristas focus on drinks." },
      { role: "Baker / Kitchen Prep", count: lean ? 0 : 1, hourlyRate: "$14–$18", monthlyEstimate: lean ? 0 : 2600, priority: lean ? "optional" : "recommended", note: "Only if you serve fresh pastries or food in-house." },
    ].filter(r => r.count > 0);
  }

  // Bakery
  if (bt === "bakery" || bt === "bakery-cafe") {
    return [
      { role: "Head Baker", count: 1, hourlyRate: "$18–$26", monthlyEstimate: 3800, priority: "essential", note: "Starts at 3–5 AM. Sets recipes and production schedule." },
      { role: "Baker / Pastry Assistant", count: lean ? 0 : 2, hourlyRate: "$14–$19", monthlyEstimate: lean ? 0 : 5200, priority: lean ? "optional" : "essential", note: "Helps with volume. Essential for variety." },
      { role: "Counter / Sales Staff", count: lean ? 1 : 2, hourlyRate: "$12–$16", monthlyEstimate: lean ? 2000 : 4000, priority: "essential", note: "Handles customer orders, packaging, and upselling." },
      { role: "Decorator / Cake Artist", count: lean ? 0 : 1, hourlyRate: "$16–$22", monthlyEstimate: lean ? 0 : 3200, priority: "optional", note: "Only if you offer custom cakes or specialty items." },
    ].filter(r => r.count > 0);
  }

  // Pizzeria
  if (bt === "pizzeria") {
    return [
      { role: "Pizza Maker / Pizzaiolo", count: lean ? 1 : 2, hourlyRate: "$16–$22", monthlyEstimate: lean ? 3200 : 6400, priority: "essential", note: "The heart of your operation. Experience matters." },
      { role: "Prep Cook", count: 1, hourlyRate: "$13–$17", monthlyEstimate: 2400, priority: "essential", note: "Prepares dough, sauces, and toppings daily." },
      { role: "Cashier / Counter", count: lean ? 1 : 2, hourlyRate: "$12–$15", monthlyEstimate: lean ? 2000 : 4000, priority: "essential", note: "Takes orders, handles phone and online orders." },
      { role: "Delivery Driver", count: lean ? 1 : 2, hourlyRate: "$10–$14 + tips", monthlyEstimate: lean ? 1800 : 3600, priority: "recommended", note: "If offering delivery. Consider using DoorDash/Uber instead." },
      { role: "Dishwasher", count: 1, hourlyRate: "$12–$15", monthlyEstimate: 2000, priority: "essential", note: "Pans, trays, and dishes pile up fast in a pizzeria." },
    ].filter(r => r.count > 0);
  }

  // Food Truck
  if (bt === "food-truck") {
    return [
      { role: "Owner / Operator (You)", count: 1, hourlyRate: "N/A", monthlyEstimate: 0, priority: "essential", note: "You'll be cooking, serving, and driving at first." },
      { role: "Cook / Assistant", count: lean ? 0 : 1, hourlyRate: "$14–$18", monthlyEstimate: lean ? 0 : 2800, priority: lean ? "optional" : "essential", note: "Helps during events and lunch rushes." },
      { role: "Cashier / Window Server", count: lean ? 0 : 1, hourlyRate: "$12–$15", monthlyEstimate: lean ? 0 : 2000, priority: lean ? "optional" : "recommended", note: "Takes orders while you cook." },
    ].filter(r => r.count > 0);
  }

  // Juice / Smoothie Bar
  if (bt === "juice-bar") {
    return [
      { role: "Lead Juicer / Blender", count: 1, hourlyRate: "$14–$18", monthlyEstimate: 2800, priority: "essential", note: "Knows recipes, ingredients, and prep procedures." },
      { role: "Counter Staff", count: lean ? 1 : 2, hourlyRate: "$12–$16", monthlyEstimate: lean ? 2000 : 4000, priority: "essential", note: "Takes orders, preps bowls, handles register." },
      { role: "Prep / Stock", count: lean ? 0 : 1, hourlyRate: "$12–$15", monthlyEstimate: lean ? 0 : 2000, priority: lean ? "optional" : "recommended", note: "Washes, cuts, and preps fresh produce daily." },
    ].filter(r => r.count > 0);
  }

  // Ice Cream Shop
  if (bt === "ice-cream-shop") {
    return [
      { role: "Ice Cream Maker", count: lean ? 0 : 1, hourlyRate: "$15–$20", monthlyEstimate: lean ? 0 : 3000, priority: lean ? "optional" : "essential", note: "Only if making in-house. Otherwise buy wholesale." },
      { role: "Scooper / Counter Staff", count: lean ? 2 : 3, hourlyRate: "$11–$15", monthlyEstimate: lean ? 3600 : 5400, priority: "essential", note: "Fast, friendly service. Great for part-time students." },
      { role: "Shift Lead", count: lean ? 0 : 1, hourlyRate: "$14–$17", monthlyEstimate: lean ? 0 : 2600, priority: lean ? "optional" : "recommended", note: "Opens/closes shop when you're not there." },
    ].filter(r => r.count > 0);
  }

  // Fast Casual
  if (bt === "fast-casual") {
    return [
      { role: "Kitchen Lead", count: 1, hourlyRate: "$16–$22", monthlyEstimate: 3400, priority: "essential", note: "Runs the line and maintains food quality." },
      { role: "Line Cook", count: lean ? 1 : 2, hourlyRate: "$14–$18", monthlyEstimate: lean ? 2600 : 5200, priority: "essential", note: "Assembles orders on the line." },
      { role: "Cashier / Order Taker", count: lean ? 1 : 2, hourlyRate: "$12–$15", monthlyEstimate: lean ? 2000 : 4000, priority: "essential", note: "Handles register, online orders, and customer flow." },
      { role: "Prep Cook", count: lean ? 0 : 1, hourlyRate: "$13–$16", monthlyEstimate: lean ? 0 : 2400, priority: lean ? "optional" : "recommended", note: "Morning prep to keep the line stocked all day." },
      { role: "Dishwasher", count: 1, hourlyRate: "$12–$14", monthlyEstimate: 1900, priority: "essential", note: "Keeps trays, pans, and utensils clean." },
    ].filter(r => r.count > 0);
  }

  // Beauty Salon
  if (bt === "beauty-salon") {
    return [
      { role: "Lead Stylist", count: 1, hourlyRate: "$18–$28", monthlyEstimate: 3800, priority: "essential", note: "Sets service standards and brings clientele." },
      { role: "Stylist", count: lean ? 1 : 3, hourlyRate: "$15–$25", monthlyEstimate: lean ? 3200 : 9600, priority: "essential", note: "Revenue drivers. Consider booth rental vs. salary." },
      { role: "Shampoo Assistant", count: lean ? 0 : 1, hourlyRate: "$11–$14", monthlyEstimate: lean ? 0 : 1900, priority: lean ? "optional" : "recommended", note: "Washes, preps clients. Frees up stylists." },
      { role: "Receptionist", count: lean ? 0 : 1, hourlyRate: "$13–$17", monthlyEstimate: lean ? 0 : 2400, priority: lean ? "optional" : "recommended", note: lean ? "Use online booking (Fresha, Square)." : "Handles calls, scheduling, and retail sales." },
    ].filter(r => r.count > 0);
  }

  // Barber Shop
  if (bt === "barber-shop") {
    return [
      { role: "Lead Barber", count: 1, hourlyRate: "$18–$30", monthlyEstimate: 4000, priority: "essential", note: "Sets the shop culture and brings regulars." },
      { role: "Barber", count: lean ? 1 : 3, hourlyRate: "$15–$25", monthlyEstimate: lean ? 3200 : 9600, priority: "essential", note: "Chair rental ($200–$400/wk) or commission (50–60%) model." },
      { role: "Receptionist / Cashier", count: lean ? 0 : 1, hourlyRate: "$12–$16", monthlyEstimate: lean ? 0 : 2200, priority: lean ? "optional" : "recommended", note: lean ? "Use walk-ins and Booksy/Square." : "Manages walk-ins and appointments." },
    ].filter(r => r.count > 0);
  }

  // Nail Salon
  if (bt === "nail-salon") {
    return [
      { role: "Lead Nail Technician", count: 1, hourlyRate: "$16–$24", monthlyEstimate: 3400, priority: "essential", note: "Your best technician. Handles complex work." },
      { role: "Nail Technician", count: lean ? 1 : 3, hourlyRate: "$13–$20", monthlyEstimate: lean ? 2600 : 7800, priority: "essential", note: "1 tech per chair. Commission or hourly + tips." },
      { role: "Receptionist", count: lean ? 0 : 1, hourlyRate: "$12–$16", monthlyEstimate: lean ? 0 : 2200, priority: lean ? "optional" : "recommended", note: lean ? "Use online booking apps." : "Books appointments and handles payments." },
    ].filter(r => r.count > 0);
  }

  // Fitness Studio
  if (bt === "fitness-studio") {
    return [
      { role: "Head Instructor / Trainer", count: 1, hourlyRate: "$25–$50/class", monthlyEstimate: 4000, priority: "essential", note: "Teaches most classes and sets programming." },
      { role: "Instructor", count: lean ? 0 : 2, hourlyRate: "$20–$40/class", monthlyEstimate: lean ? 0 : 5000, priority: lean ? "optional" : "essential", note: "Cover different class types and time slots." },
      { role: "Front Desk", count: lean ? 0 : 1, hourlyRate: "$13–$17", monthlyEstimate: lean ? 0 : 2400, priority: lean ? "optional" : "recommended", note: lean ? "Use Mindbody or Vagaro for self check-in." : "Handles check-ins, memberships, and retail." },
    ].filter(r => r.count > 0);
  }

  // Hotel & Lodging
  if (bt === "hotel") {
    const rooms = seats || 50; // re-use seats as rooms count
    return [
      { role: "General Manager", count: 1, hourlyRate: "$28–$45", monthlyEstimate: 6500, priority: "essential", note: "Oversees all departments and P&L." },
      { role: "Front Desk Agent", count: lean ? 2 : 4, hourlyRate: "$14–$18", monthlyEstimate: lean ? 4800 : 9600, priority: "essential", note: "24/7 coverage requires multiple shifts." },
      { role: "Housekeeping Staff", count: lean ? Math.max(2, Math.floor(rooms / 20)) : Math.max(4, Math.floor(rooms / 12)), hourlyRate: "$12–$16", monthlyEstimate: lean ? 4200 : 8400, priority: "essential", note: `~${lean ? 20 : 12} rooms per housekeeper per shift.` },
      { role: "Housekeeping Supervisor", count: lean ? 0 : 1, hourlyRate: "$16–$22", monthlyEstimate: lean ? 0 : 3400, priority: lean ? "optional" : "recommended", note: "Inspects rooms and manages laundry." },
      { role: "Maintenance Engineer", count: 1, hourlyRate: "$18–$25", monthlyEstimate: 3600, priority: "essential", note: "HVAC, plumbing, electrical, general repairs." },
      { role: "Night Auditor", count: 1, hourlyRate: "$15–$20", monthlyEstimate: 2800, priority: "essential", note: "Runs overnight front desk + closes daily books." },
      { role: "Concierge", count: lean ? 0 : 1, hourlyRate: "$15–$20", monthlyEstimate: lean ? 0 : 3000, priority: lean ? "optional" : "recommended", note: "Guest services, restaurant reservations, tours." },
      { role: "Revenue / Reservations Manager", count: lean ? 0 : 1, hourlyRate: "$22–$35", monthlyEstimate: lean ? 0 : 5200, priority: lean ? "optional" : "recommended", note: "Manages OTA pricing, direct bookings, yield." },
      { role: "F&B Manager", count: data.outlets?.length ? 1 : 0, hourlyRate: "$20–$30", monthlyEstimate: data.outlets?.length ? 4500 : 0, priority: data.outlets?.length ? "essential" : "optional", note: "Oversees all restaurant/bar outlets in the property." },
      { role: "Security", count: lean ? 0 : 1, hourlyRate: "$14–$20", monthlyEstimate: lean ? 0 : 2800, priority: lean ? "optional" : "recommended", note: "Overnight and event security." },
    ].filter(r => r.count > 0);
  }

  // Bed & Breakfast — owner-operated lodging with light F&B (breakfast).
  // Headcount target: 3–8 staff. Avoids hotel-scale roles like Revenue
  // Manager and Concierge that would distort cost projections for a 4–12 room
  // operation.
  if (bt === "bed-and-breakfast") {
    return [
      { role: "Owner / Innkeeper", count: 1, hourlyRate: "N/A", monthlyEstimate: 0, priority: "essential", note: "Lives on-site or nearby. Handles check-ins, hosting, and guest experience." },
      { role: "Breakfast Cook", count: 1, hourlyRate: "$15–$22", monthlyEstimate: 2600, priority: "essential", note: "Morning shift only. Preps and serves breakfast for guests." },
      { role: "Housekeeper", count: lean ? 1 : 2, hourlyRate: "$13–$17", monthlyEstimate: lean ? 2200 : 4400, priority: "essential", note: "Cleans rooms, handles linens and laundry between stays." },
      { role: "Front Desk / Reservations", count: lean ? 0 : 1, hourlyRate: "$13–$17", monthlyEstimate: lean ? 0 : 2400, priority: lean ? "optional" : "recommended", note: lean ? "Owner handles bookings via OTA + email." : "Manages bookings, OTA channels, and guest communication." },
      { role: "Maintenance / Handyperson", count: lean ? 0 : 1, hourlyRate: "$16–$22", monthlyEstimate: lean ? 0 : 1800, priority: lean ? "optional" : "recommended", note: "Part-time. Handles repairs, gardening, seasonal upkeep." },
    ].filter(r => r.count > 0);
  }

  // Private Events & Catering — event-driven crew. Variable headcount based on
  // booking volume; the steady-state team is small (3–10).
  if (bt === "private-events") {
    return [
      { role: "Catering Chef", count: 1, hourlyRate: "$22–$32", monthlyEstimate: 4400, priority: "essential", note: "Designs menus per event and leads the kitchen on-site." },
      { role: "Sous Chef / Prep Lead", count: lean ? 0 : 1, hourlyRate: "$18–$24", monthlyEstimate: lean ? 0 : 3200, priority: lean ? "optional" : "recommended", note: "Handles prep day-before and supports execution day-of." },
      { role: "Event Captain", count: 1, hourlyRate: "$20–$28", monthlyEstimate: 3600, priority: "essential", note: "Runs front-of-house at each event. Client liaison." },
      { role: "Server", count: lean ? 2 : 4, hourlyRate: "$16–$22 + tips", monthlyEstimate: lean ? 3200 : 6400, priority: "essential", note: "1 server per ~20 guests. Hire reliable on-call roster." },
      { role: "Bartender", count: lean ? 1 : 2, hourlyRate: "$18–$25 + tips", monthlyEstimate: lean ? 2000 : 4000, priority: "recommended", note: "Required when alcohol service is part of the package." },
      { role: "Sales / Booking Coordinator", count: lean ? 0 : 1, hourlyRate: "$20–$28", monthlyEstimate: lean ? 0 : 3800, priority: lean ? "optional" : "recommended", note: lean ? "Owner handles inquiries and contracts." : "Converts inquiries, manages calendar, builds proposals." },
    ].filter(r => r.count > 0);
  }

  // Private Chef — solo or duo operation. NEVER includes Executive Chef or
  // Head Chef titles (the chef IS the brand). Designed for 1–2 staff.
  if (bt === "private-chef") {
    return [
      { role: "Private Chef (You)", count: 1, hourlyRate: "$50–$150/event", monthlyEstimate: 0, priority: "essential", note: "You're the brand. Cooks, plates, and engages directly with the host." },
      { role: "Sous / Kitchen Assistant", count: lean ? 0 : 1, hourlyRate: "$18–$28", monthlyEstimate: lean ? 0 : 1600, priority: lean ? "optional" : "recommended", note: "On-call only. Helps with prep, plating, and dish handling at larger events." },
    ].filter(r => r.count > 0);
  }

  // Virtual / Ghost Kitchen — delivery-only. No FOH, lean BOH crew of 2–5.
  if (bt === "ghost-kitchen") {
    return [
      { role: "Kitchen Lead", count: 1, hourlyRate: "$18–$24", monthlyEstimate: 3600, priority: "essential", note: "Runs the line, manages tickets across delivery apps." },
      { role: "Line Cook", count: lean ? 1 : 2, hourlyRate: "$15–$20", monthlyEstimate: lean ? 2800 : 5600, priority: "essential", note: "Executes orders for one or more virtual brands." },
      { role: "Packager / Expediter", count: lean ? 0 : 1, hourlyRate: "$13–$16", monthlyEstimate: lean ? 0 : 2200, priority: lean ? "optional" : "recommended", note: "Bags, labels, and hands off to drivers. Critical at peak." },
      { role: "Operations Manager (part-time)", count: lean ? 0 : 1, hourlyRate: "$22–$30", monthlyEstimate: lean ? 0 : 2400, priority: lean ? "optional" : "recommended", note: "Manages multi-platform pricing, promotions, and ratings." },
    ].filter(r => r.count > 0);
  }

  // Default retail / other
  return [
    { role: "Store Manager", count: 1, hourlyRate: "$18–$26", monthlyEstimate: 3600, priority: "essential", note: lean ? "This will be you." : "Handles daily operations and team." },
    { role: "Sales Associate", count: lean ? 1 : 2, hourlyRate: "$13–$18", monthlyEstimate: lean ? 2400 : 4800, priority: "essential", note: "Cover peak hours. Consider part-time first." },
    { role: "Stock / Inventory", count: lean ? 0 : 1, hourlyRate: "$12–$16", monthlyEstimate: lean ? 0 : 2200, priority: lean ? "optional" : "recommended", note: lean ? "Handle this yourself or split with associates." : "Keeps shelves stocked and organized." },
  ].filter(r => r.count > 0);
}

function generateMilestones(data: OnboardingData): MilestoneItem[] {
  const isFoodBiz = ["restaurant", "coffee-shop", "bakery", "bakery-cafe", "ice-cream-shop", "bar", "wine-shop", "pizzeria", "juice-bar", "food-truck", "fast-casual", "brunch-spot"].includes(data.businessType);
  const isHotel = data.businessType === "hotel";
  const milestones: MilestoneItem[] = [
    { label: "Business entity registered", weeksBeforeOpen: isHotel ? 52 : 20, critical: true },
    { label: "Lease signed", weeksBeforeOpen: isHotel ? 44 : 16, critical: true },
    { label: "All permits submitted", weeksBeforeOpen: isHotel ? 40 : 14, critical: true },
    { label: "Floor plan finalized", weeksBeforeOpen: isHotel ? 36 : 13, critical: true },
    { label: "Buildout starts", weeksBeforeOpen: isHotel ? 32 : 12, critical: true },
    { label: "Equipment ordered", weeksBeforeOpen: isHotel ? 24 : 10, critical: true },
    { label: "Branding & signage ready", weeksBeforeOpen: 8, critical: false },
    { label: "Staff hiring begins", weeksBeforeOpen: isHotel ? 12 : 6, critical: true },
    { label: "Marketing launch", weeksBeforeOpen: 4, critical: false },
    { label: "Staff training complete", weeksBeforeOpen: 2, critical: true },
    { label: "Soft opening (friends & family)", weeksBeforeOpen: 1, critical: true },
    { label: "Grand opening", weeksBeforeOpen: 0, critical: true },
  ];
  if (isFoodBiz || isHotel) {
    milestones.splice(4, 0, { label: "Health inspection scheduled", weeksBeforeOpen: isHotel ? 30 : 12, critical: true });
    milestones.splice(7, 0, { label: "Supplier accounts set up", weeksBeforeOpen: 8, critical: true });
  }
  if (data.businessType.includes("bar") || data.businessType.includes("wine")) {
    milestones.splice(3, 0, { label: "Alcohol license application submitted", weeksBeforeOpen: 20, critical: true });
  }
  if (isHotel) {
    milestones.splice(3, 0, { label: "Lodging license application submitted", weeksBeforeOpen: 48, critical: true });
    milestones.splice(4, 0, { label: "Fire safety & elevator inspections scheduled", weeksBeforeOpen: 36, critical: true });
    milestones.splice(5, 0, { label: "PMS & key card systems ordered", weeksBeforeOpen: 28, critical: true });
    milestones.splice(8, 0, { label: "FF&E procurement complete", weeksBeforeOpen: 16, critical: true });
    if (data.outlets?.length) {
      milestones.splice(9, 0, { label: `Outlet buildout started (${data.outlets.map(o => o.name).join(", ")})`, weeksBeforeOpen: 20, critical: true });
    }
  }
  return milestones;
}

// Realistic base startup cost ranges (USD) by business type — US national average.
// Where a profile is defined in `businessTypeProfiles.ts`, the profile value
// takes precedence (see `getStartupCostRange` below). This map is the legacy
// fallback for types that don't yet have a profile (retail, beauty, etc.).
const BASE_STARTUP_COSTS: Record<string, { low: number; high: number }> = {
  "restaurant":       { low: 350000, high: 850000 },
  "brunch-spot":      { low: 300000, high: 700000 },
  "pizzeria":         { low: 280000, high: 650000 },
  "coffee-shop":      { low: 150000, high: 380000 },
  "bakery":           { low: 120000, high: 300000 },
  "bakery-cafe":      { low: 160000, high: 380000 },
  "bar":              { low: 250000, high: 600000 },
  "wine-shop":        { low: 200000, high: 480000 },
  "fast-casual":      { low: 200000, high: 500000 },
  "juice-bar":        { low: 120000, high: 280000 },
  "ice-cream-shop":   { low: 100000, high: 250000 },
  "food-truck":       { low: 60000,  high: 180000 },
  "hotel":            { low: 2000000, high: 8000000 },
  "beauty-salon":     { low: 80000,  high: 220000 },
  "barber-shop":      { low: 60000,  high: 180000 },
  "nail-salon":       { low: 70000,  high: 200000 },
  "fitness-studio":   { low: 100000, high: 350000 },
  "retail-boutique":  { low: 80000,  high: 250000 },
  "convenience-store":{ low: 80000,  high: 300000 },
  "flower-shop":      { low: 60000,  high: 180000 },
  "pet-store":        { low: 100000, high: 300000 },
  "grocery-store":    { low: 150000, high: 500000 },
  "furniture-store":  { low: 120000, high: 400000 },
  "bookstore":        { low: 80000,  high: 250000 },
  "pharmacy":         { low: 150000, high: 400000 },
  "pop-up-shop":      { low: 15000,  high: 60000 },
};

/**
 * Resolves the base US startup cost range for a business type, preferring the
 * centralized profile (per the customization spec) and falling back to the
 * legacy table for types without a profile.
 */
function getStartupCostRange(businessTypeId: string): { low: number; high: number } {
  const profile = getBusinessTypeProfile(businessTypeId);
  if (profile) return { low: profile.startupUSD.low, high: profile.startupUSD.high };
  return BASE_STARTUP_COSTS[businessTypeId] || { low: 120000, high: 350000 };
}

// City cost multiplier relative to US average (1.0)
function getCityCostMultiplier(city: string): number {
  // Delegates to the centralized JLL/CBRE/T+T 2025 multiplier table
  // (Miami = 1.00). See src/lib/cityMultipliers.ts.
  return getCityMultiplier(city);
}

export function generateMockResults(data: OnboardingData) {
  // Hotel & B&B are room-driven. Derive seat-equivalents so the existing
  // staffing / revenue / forecast pipelines (which are seat-based) produce
  // sensible numbers without a parallel codepath.
  //   - Hotel F&B: ~1.5 restaurant/bar seats per guest room
  //   - B&B breakfast room: ~2 seats per guest room (double occupancy)
  if (data.businessType === "hotel" || data.businessType === "bed-and-breakfast") {
    const totalRooms = (data.guestRooms ?? []).reduce(
      (sum, r) => sum + (Number(r.quantity) || 0),
      0,
    );
    if (totalRooms > 0) {
      const seatsPerRoom = data.businessType === "hotel" ? 1.5 : 2;
      const derivedSeats = Math.max(1, Math.round(totalRooms * seatsPerRoom));
      // Only auto-populate when the user hasn't entered seat values manually.
      if (!data.seats) data = { ...data, seats: derivedSeats };
      if (!data.indoorSeats) data = { ...data, indoorSeats: derivedSeats };
    }
  }

  // Private Chef — event-driven. Derive a seat-equivalent so seat-based
  // pipelines (sqft, staffing) still produce sensible numbers.
  // Seat-equivalent ≈ guests × events / ~26 working days.
  if (data.businessType === "private-chef") {
    const guests = Number(data.guestsPerEvent) || 0;
    const events = Number(data.eventsPerMonth) || 0;
    if (guests > 0 && events > 0) {
      const monthlyCovers = guests * events;
      const derivedSeats = Math.max(1, Math.round(monthlyCovers / 26));
      if (!data.seats) data = { ...data, seats: derivedSeats };
      if (!data.indoorSeats) data = { ...data, indoorSeats: derivedSeats };
    }
  }

  // Catering / Private Events — use max capacity per event as the staffing
  // anchor (worst-case crew), with monthly covers driving revenue elsewhere.
  if (data.businessType === "private-events") {
    const maxCap = Number(data.maxCapacityPerEvent) || 0;
    const guests = Number(data.guestsPerEvent) || 0;
    const seatAnchor = Math.max(maxCap, guests);
    if (seatAnchor > 0 && !data.seats) {
      data = { ...data, seats: seatAnchor, indoorSeats: data.indoorSeats || seatAnchor };
    }
  }

  // Food truck — counter seats are usually small/optional; the demand driver
  // is daily covers, which we map to a service-line seat-equivalent (≈ daily
  // covers / 4 turnover-equivalent units).
  if (data.businessType === "food-truck") {
    const counter = Number(data.counterSeats) || 0;
    const dailyCovers = Number(data.avgDailyCovers) || 0;
    const derived = counter > 0 ? counter : Math.max(1, Math.round(dailyCovers / 4));
    if (!data.seats && derived > 0) {
      data = { ...data, seats: derived, indoorSeats: data.indoorSeats || derived };
    }
  }

  const businessLabel = businessTypes.find(b => b.id === data.businessType)?.label || data.businessType;
  const budgetObj = budgetRanges.find(b => b.id === data.budget);
  const budgetValue = budgetObj?.value || 100000;
  // Build a city-aware budget label using local currency
  const cityBudgetOptions = getBudgetOptionsForCity(data.city);
  const localBudgetObj = cityBudgetOptions.find(b => b.id === data.budget);
  const localBudgetLabel = localBudgetObj?.label || budgetObj?.label || "";
  const cityProfile = getCityProfile(data.city);
  const { symbol: currSymbol, isUK } = getCityCurrency(data.city);

  const expMultiplier = data.experience === "experienced" ? 0.7 : data.experience === "some" ? 0.85 : 1;
  const hasFullLiquor = data.alcoholType === "full-liquor" || data.businessType.includes("bar");
  // Anchor timeline to the per-business-type profile (Restaurant 6–12 mo,
  // Café 3–6 mo, Hotel 12–24 mo, etc.). Launch speed shifts within that
  // range; experience compresses it modestly.
  const typeProfile = getBusinessTypeProfile(data.businessType);
  const profileMin = typeProfile?.timelineMonths.min ?? 4;
  const profileMax = typeProfile?.timelineMonths.max ?? 9;
  // Map launch speed to a position inside the profile band.
  const speedPos = data.launchSpeed === "fast" || data.spacePreference === "second-gen"
    ? 0.15
    : data.launchSpeed === "best-location" || data.spacePreference === "new-space"
      ? 0.85
      : 0.5;
  const monthsTarget = profileMin + (profileMax - profileMin) * speedPos;
  let totalWeeks = Math.max(4, Math.round(monthsTarget * 4 * expMultiplier));
  // Liquor licensing is the binding constraint for bars/full-liquor concepts.
  if (hasFullLiquor && !isUK) totalWeeks = Math.max(totalWeeks, 40);
  if (hasFullLiquor && isUK) totalWeeks = Math.max(totalWeeks, 24);
  // Surface the band as `min–max months` so the UI matches the profile spec.
  const timeline = hasFullLiquor && !isUK
    ? "10–14 months"
    : `${profileMin}–${profileMax} months`;

  // Use city-specific neighborhoods with real rent data
  const targetSqft = data.sqftMax || 1200;
  const neighborhoods = cityProfile.neighborhoods.map(n => ({
    ...n,
    rentRange: `${currSymbol}${Math.round(n.rentPerSqftMin * targetSqft / 12).toLocaleString()}–${currSymbol}${Math.round(n.rentPerSqftMax * targetSqft / 12).toLocaleString()}/mo`,
  }));

  const isFoodBiz = ["restaurant", "coffee-shop", "bakery", "bakery-cafe", "ice-cream-shop", "bar", "wine-shop", "pizzeria", "juice-bar", "food-truck", "fast-casual", "brunch-spot"].includes(data.businessType);
  const isBeauty = ["beauty-salon", "barber-shop", "nail-salon"].includes(data.businessType);
  const isHotel = data.businessType === "hotel";

  // Build timeline with week numbers
  let weekCursor = 0;
  const makeStep = (step: string, durationWeeks: number, done: boolean, substeps: TimelineSubstep[]): TimelineStep => {
    const s: TimelineStep = { step, duration: durationWeeks <= 1 ? "1 week" : `${durationWeeks} weeks`, done, substeps, weekStart: weekCursor, weekEnd: weekCursor + durationWeeks };
    weekCursor += durationWeeks;
    return s;
  };

  const timelineSteps: TimelineStep[] = [
    ...(hasFullLiquor ? [
      makeStep("Liquor License Application", 18, false, [
        { task: "Submit Florida DBPR liquor license application", detail: "Apply immediately — this is the longest lead-time item. The Florida SLA process takes 3–6 months minimum." },
        { task: "Complete background check and fingerprinting", detail: "All owners and managers must submit to FBI/FDLE background checks." },
        { task: "Post public notice of application", detail: "Florida requires a 45-day public notice period before license approval." },
        { task: "Prepare financial disclosure documents", detail: "Submit proof of funding, lease agreements, and ownership structure." },
        { task: "Schedule and pass final DBPR inspection", detail: "Premises must be fully built out and compliant before final approval." },
      ]),
    ] : []),
    makeStep("Idea Validation", 2, true, [
      { task: "Define your business concept", detail: "Write a one-paragraph summary of what you'll sell, who your customer is, and what makes you different." },
      { task: "Research competitors in your area", detail: "Visit 5–10 similar businesses nearby. Note their pricing, hours, menu/products, and customer volume." },
      { task: "Talk to potential customers", detail: "Survey at least 20 people who fit your target customer profile." },
      { task: "Validate demand online", detail: "Check Google Trends, Yelp reviews of competitors, and local Facebook groups." },
    ]),
    makeStep("Business Formation", 2, false, [
      { task: "Choose your business structure", detail: "Most beginners choose LLC for liability protection and tax flexibility." },
      { task: "Register your business name", detail: "File with your state's Secretary of State." },
      { task: "Get an EIN from the IRS", detail: "Apply free at irs.gov. Takes about 5 minutes online." },
      { task: "Open a business bank account", detail: "Keep personal and business finances separate." },
      { task: "Set up basic accounting", detail: "Use QuickBooks, Wave (free), or hire a bookkeeper." },
    ]),
    makeStep("Location Search", 4, false, [
      { task: "Define your location requirements", detail: `Based on your inputs, aim for ${formatAreaRange(data.sqftMin || 800, data.sqftMax || 1500, data.city)} with ${data.seatingType === "both" ? "indoor + outdoor" : data.seatingType || "indoor"} space.` },
      { task: "Search commercial listings", detail: "Use LoopNet, Crexi, or local commercial brokers." },
      { task: "Visit at least 5 spaces", detail: "Visit at different times of day. Check foot traffic, parking, noise." },
      { task: "Negotiate the lease", detail: "Ask for tenant improvement allowance, free rent months." },
      { task: "Verify zoning compliance", detail: "Call your city's zoning office before signing." },
    ]),
    makeStep("Permits & Licenses", 4, false, [
      { task: "Apply for business license", detail: "Contact your city/county clerk's office." },
      { task: "Get your sales tax permit", detail: "Apply through your state's Department of Revenue." },
      { task: "Apply for industry-specific permits", detail: isFoodBiz ? "Apply for food service license through your county health department." : isBeauty ? "Apply for cosmetology establishment license." : "Check with your city for industry-specific requirements." },
      { task: "Schedule fire inspection", detail: "Contact your local fire marshal's office." },
      { task: "Get certificate of occupancy", detail: "Applied for through your city's building department." },
    ]),
    makeStep("Design & Buildout", Math.round(6 * expMultiplier), false, [
      { task: "Hire a designer or architect", detail: "Get at least 3 quotes." },
      { task: "Create floor plan and layout", detail: `Plan for ${data.seats || 30} seats across ${formatAreaRange(data.sqftMin || 800, data.sqftMax || 1500, data.city)}.` },
      { task: "Hire contractors for buildout", detail: "Get 3+ bids. Check licenses, insurance, and references." },
      { task: "Order signage and branding", detail: "Allow 2–4 weeks for production." },
      { task: "Install utilities and systems", detail: "POS, internet, security, specialized equipment." },
    ]),
    makeStep("Equipment & Suppliers", 3, false, [
      { task: "Create equipment list", detail: "Research new vs. used options." },
      { task: "Research and select suppliers", detail: "Get quotes from at least 3 suppliers." },
      { task: "Order equipment with lead times", detail: "Some takes 4–8 weeks to deliver." },
      { task: "Set up vendor accounts", detail: "Apply for wholesale accounts." },
    ]),
    makeStep("Hiring & Training", 3, false, [
      { task: "Define roles and create job descriptions", detail: data.teamSize === "lean" ? "Start with essential roles only — you'll do the rest." : "Hire all key roles before opening." },
      { task: "Post job listings", detail: "Use Indeed, local Facebook groups, and word-of-mouth." },
      { task: "Interview and hire team", detail: "Prioritize attitude and reliability." },
      { task: "Set up payroll", detail: "Use Gusto, Square Payroll, or ADP." },
      { task: "Train your team", detail: `Plan ${data.serviceStyle === "full-service" ? "2 weeks" : "1 week"} of training minimum.` },
    ]),
    makeStep("Marketing & Pre-Opening", 3, false, [
      { task: "Create social media accounts", detail: "Instagram, Facebook, Google Business, TikTok." },
      { task: "Build a simple website", detail: "Include address, hours, menu/services." },
      { task: "Plan grand opening event", detail: "Offer special deals, invite local media." },
      { task: "Set up Google Business Profile", detail: "Free and critical for local search." },
      { task: "Order marketing materials", detail: "Cards, flyers, banners." },
    ]),
    makeStep("Soft Opening", 1, false, [
      { task: "Friends & family test run", detail: "Invite 20–50 people. Collect honest feedback." },
      { task: "Test all systems", detail: "Ordering, payment, closing procedures." },
      { task: "Fix issues", detail: "Address every complaint before public opening." },
    ]),
    makeStep("Grand Opening", 1, false, [
      { task: "Execute opening day plan", detail: "Arrive early, brief your team, open with energy!" },
      { task: "Document everything for social", detail: "Photos, videos, live updates." },
      { task: "Collect customer feedback", detail: "Comment cards or digital survey." },
    ]),
  ];

  // Use real city-specific permit data when available, fall back to country-generic, then US-generic
  const cityPermits = getCityPermits(data.city, data.alcoholType, isFoodBiz);
  
  let permits: PermitDetail[];
  if (cityPermits.length > 0) {
    permits = cityPermits;
  } else {
    // Try country-level generic permits
    const cityData = getCityData(data.city);
    const countryConfig = cityData ? getCountryGenericPermits(cityData.country) : null;
    if (countryConfig) {
      permits = getCityPermits(data.city, data.alcoholType, isFoodBiz).length > 0
        ? getCityPermits(data.city, data.alcoholType, isFoodBiz)
        : countryConfig.permits;
    } else {
      // Final fallback — US-generic
      permits = [
        { name: "EIN (Federal Tax ID)", simple: "Free federal tax ID — required for all US businesses.", required: true, link: "https://www.irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online", processingTime: "Instant online", estimatedCost: "FREE", requirements: ["SSN or ITIN of responsible party", "Business name and address", "Business structure type"] },
        { name: "Business Registration / LLC", simple: "Register your business name and entity with the state.", required: true, link: "https://www.sba.gov/business-guide/launch-your-business/register-your-business", processingTime: "3–10 business days", estimatedCost: "$50–$500 (varies by state)", requirements: ["Articles of Organization", "Filing fee payment", "Registered agent information"] },
        { name: "Sales Tax Permit", simple: "Lets you collect and remit sales tax.", required: true, link: "https://www.sba.gov/business-guide/manage-your-business/pay-taxes", processingTime: "Instant to 2 weeks", estimatedCost: "FREE in most states", requirements: ["EIN", "Business address", "Owner identification"] },
        { name: "Certificate of Occupancy", simple: "Confirms the space can legally be used for your business type.", required: true, link: "https://www.sba.gov/business-guide/launch-your-business/apply-for-licenses-and-permits", processingTime: "30–90 days", estimatedCost: "$100–$700", requirements: ["Building inspection passed", "Fire inspection passed", "Zoning compliance verified"] },
        { name: "Food Service License", simple: "Required if you serve any food or beverages.", required: isFoodBiz, link: "https://www.fda.gov/food/retail-food-industrie/retail-food-protection", processingTime: "30–60 days (inspection required)", estimatedCost: "$200–$1,000/yr", requirements: ["Health department application", "Floor plan", "Menu", "Equipment list", "Health inspection"] },
        { name: "Health Department Approval", simple: "Health inspector visits before you can open.", required: isFoodBiz || isBeauty, link: "https://www.cdc.gov/nceh/ehs/activities/food.html", processingTime: "30–45 days", estimatedCost: "$200–$500/yr", requirements: ["Facility layout plans", "Handwashing stations", "Equipment list", "Pest control plan"] },
        { name: "Alcohol License — Beer & Wine", simple: "Beer & wine license for on-premise consumption.", required: data.alcoholType === "beer-wine" || data.businessType.includes("wine"), link: "https://www.ttb.gov/alcohol/beverage-alcohol", processingTime: "45–90 days", estimatedCost: "$300–$1,500/yr", requirements: ["State alcohol board application", "Background check", "Proof of premises"] },
        { name: "Alcohol License — Full Liquor", simple: "Full liquor license for spirits and cocktails.", required: data.alcoholType === "full-liquor" || data.businessType.includes("bar"), link: "https://www.ttb.gov/alcohol/beverage-alcohol", processingTime: "60–120 days", estimatedCost: "$1,000–$14,000+/yr", requirements: ["State alcohol board application", "Background check", "Financial disclosure", "Public notice posting"] },
        { name: "Sign Permit", simple: "Permission to put up your business sign.", required: true, link: "https://www.sba.gov/business-guide/launch-your-business/apply-for-licenses-and-permits", processingTime: "15–30 days", estimatedCost: "$75–$500", requirements: ["Sign design with dimensions", "Property owner approval"] },
        { name: "Food Manager Certification", simple: "At least one certified food safety manager must be on-site.", required: isFoodBiz, link: "https://www.fda.gov/food", processingTime: "Exam: schedule anytime", estimatedCost: "$20–$200 (exam fee)", requirements: ["Pass approved food safety exam (ServSafe)", "Renews every 5 years"] },
        { name: "Fire Inspection Certificate", simple: "Fire department checks safety systems.", required: true, link: "https://www.usfa.fema.gov/prevention/outreach/", processingTime: "30–60 days", estimatedCost: "$50–$500", requirements: ["Fire alarm system", "Exit signage", "Fire extinguishers", "Occupancy capacity posted"] },
      ];
    }
  }

  // Add hotel-specific permits
  if (isHotel) {
    permits.push(
      { name: "Lodging License", simple: "State/county license required to operate a hotel, motel, or B&B.", required: true, link: "https://www.sba.gov/business-guide/launch-your-business/apply-for-licenses-and-permits", processingTime: "30–90 days", estimatedCost: "$200–$2,000/yr", requirements: ["Application to state hospitality board", "Building inspection", "Room count declaration", "Fire safety compliance"] },
      { name: "Liquor License (Hotel)", simple: "Required if the hotel bar, restaurant, or minibar serves alcohol.", required: data.alcoholType !== "none", link: "https://www.ttb.gov/alcohol/beverage-alcohol", processingTime: "60–120 days", estimatedCost: "$1,000–$14,000+/yr", requirements: ["State alcohol board application", "Background check", "Proof of premises", "Separate license per outlet may be required"] },
      { name: "Fire Safety Certificate", simple: "Comprehensive fire safety inspection for multi-story lodging.", required: true, link: "https://www.usfa.fema.gov/prevention/outreach/", processingTime: "30–60 days", estimatedCost: "$200–$1,500", requirements: ["Sprinkler system certification", "Smoke & CO detectors in all rooms", "Fire alarm panel", "Emergency evacuation plan posted", "Annual re-inspection"] },
      { name: "Elevator Inspection Certificate", simple: "Required for properties with elevators — annual inspection.", required: true, link: "https://www.osha.gov/elevators", processingTime: "30–45 days", estimatedCost: "$200–$500/yr per elevator", requirements: ["State elevator board registration", "Annual safety inspection", "Maintenance contract on file"] },
      { name: "Pool & Spa Permit", simple: "Health department permit for hotel swimming pool and/or spa facilities.", required: true, link: "https://www.cdc.gov/healthywater/swimming/", processingTime: "30–60 days", estimatedCost: "$100–$500/yr", requirements: ["Pool safety fence/barrier", "Water quality testing schedule", "Certified pool operator on staff", "AED on premises"] },
      { name: "Health Department Certification", simple: "Comprehensive health inspection covering kitchen, laundry, and guest rooms.", required: true, link: "https://www.cdc.gov/nceh/ehs/activities/food.html", processingTime: "30–60 days", estimatedCost: "$300–$1,000/yr", requirements: ["Kitchen & food handling compliance", "Laundry sanitation standards", "Guest room hygiene protocols", "Pest control plan"] },
      { name: "ADA Compliance Certification", simple: "Americans with Disabilities Act compliance — accessible rooms, entrances, and facilities.", required: true, link: "https://www.ada.gov/topics/intro-to-ada/", processingTime: "Varies", estimatedCost: "$500–$10,000+ (retrofit costs)", requirements: ["Accessible parking spaces", "ADA-compliant rooms (min 5% of inventory)", "Accessible entrances and elevators", "Visual alarms and TTY phones"] },
    );
  }

  // ── Profile-driven permit clamp ──
  // Each business type publishes a permit count band (e.g. Café 5–7,
  // Pizzeria 6–8, Private Chef 2–4). Trim or pad the list so what we display
  // matches the spec the user expects in the Opening Plan.
  if (typeProfile) {
    const cap = typeProfile.permitsRange.max;
    const floor = typeProfile.permitsRange.min;
    if (permits.length > cap) {
      // Keep required permits first, then pad up to cap with optional ones.
      const required = permits.filter(p => p.required);
      const optional = permits.filter(p => !p.required);
      permits = [...required, ...optional].slice(0, cap);
    }
    if (permits.length < floor) {
      // Mark the most likely candidates as required so the count reflects the
      // realistic minimum for this concept.
      const needed = floor - permits.length;
      const promote = permits
        .filter(p => !p.required)
        .slice(0, needed)
        .map(p => p.name);
      permits = permits.map(p =>
        promote.includes(p.name) ? { ...p, required: true } : p,
      );
    }
  }

  // --- Realistic cost breakdown based on business type + city ---
  const baseCosts = getStartupCostRange(data.businessType);
  const cityMult = getCityCostMultiplier(data.city);
  // Size adjustment: larger spaces cost more
  const sqftMid = ((data.sqftMin || 800) + (data.sqftMax || 1500)) / 2;
  const sizeRef: Record<string, number> = {
    "restaurant": 1200, "coffee-shop": 900, "bakery": 800, "bar": 1000,
    "fast-casual": 1100, "hotel": 15000, "retail-boutique": 1000,
  };
  const refSqft = sizeRef[data.businessType] || 1000;
  // Scale linearly with size — no upper cap so calculations remain
  // accurate for any sqft the user enters (500 → 50,000+).
  const sizeMult = Math.max(0.01, sqftMid / refSqft);

  const adjustedLow = Math.round(baseCosts.low * cityMult * sizeMult);
  const adjustedHigh = Math.round(baseCosts.high * cityMult * sizeMult);
  // Pick a midpoint for category breakdown
  const midEstimate = (adjustedLow + adjustedHigh) / 2;

  // Hash-based jitter to avoid round numbers
  const hashSeed = (s: string) => {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
    return Math.abs(h);
  };
  const jitter = (base: number, label: string) => {
    const h = hashSeed(label + data.city + data.businessType);
    const offset = ((h % 60) - 30) / 1000; // ±3%
    return Math.round(midEstimate * (base / 100 + offset));
  };

  // Percentage allocation varies by business type
  const isRetail = ["retail-boutique", "convenience-store", "flower-shop", "pet-store", "grocery-store", "furniture-store", "bookstore", "pharmacy"].includes(data.businessType);
  const depositsPct = isHotel ? 12 : isRetail ? 12 : 15;
  const buildoutPct = isHotel ? 30 : data.spacePreference === "second-gen" ? 18 : 25;
  const equipmentPct = isHotel ? 18 : isFoodBiz ? 20 : 15;
  const inventoryPct = isHotel ? 5 : isFoodBiz ? 10 : isRetail ? 18 : 8;
  const licensePct = isHotel ? 3 : hasFullLiquor ? 6 : 4;
  const insurancePct = 3;
  const marketingPct = isHotel ? 5 : 7;
  const payrollPct = isHotel ? 8 : 5;
  const contingencyPct = 100 - depositsPct - buildoutPct - equipmentPct - inventoryPct - licensePct - insurancePct - marketingPct - payrollPct;

  const costBreakdown = [
    { category: "Deposits & Rent", estimate: jitter(depositsPct, "deposits"), percentage: depositsPct },
    { category: "Licenses & Legal", estimate: jitter(licensePct, "licenses"), percentage: licensePct },
    { category: "Design & Buildout", estimate: jitter(buildoutPct, "buildout"), percentage: buildoutPct },
    { category: "Equipment", estimate: jitter(equipmentPct, "equipment"), percentage: equipmentPct },
    { category: "Inventory", estimate: jitter(inventoryPct, "inventory"), percentage: inventoryPct },
    { category: "Insurance", estimate: jitter(insurancePct, "insurance"), percentage: insurancePct },
    { category: "Marketing", estimate: jitter(marketingPct, "marketing"), percentage: marketingPct },
    { category: "Payroll (pre-open)", estimate: jitter(payrollPct, "payroll"), percentage: payrollPct },
    { category: "Contingency", estimate: jitter(contingencyPct, "contingency"), percentage: contingencyPct },
  ];

  const totalEstimate = costBreakdown.reduce((sum, c) => sum + c.estimate, 0);
  const budgetFit = totalEstimate <= budgetValue ? "strong" : totalEstimate <= budgetValue * 1.2 ? "balanced" : "underfunded";

  const checklist: ChecklistItem[] = [
    { id: "choose-business-type", text: "Choose your business type", done: true, tip: "You've already done this!", sources: [{ name: "SBA Business Guide", url: "https://www.sba.gov/business-guide" }] },
    { id: "define-budget", text: "Define your budget", done: true, tip: "Revisit monthly to stay on track.", sources: [{ name: "SCORE Budget Template", url: "https://www.score.org/resource/financial-projections-template" }] },
    { id: "form-company", text: "Form your company (LLC, Corp, etc.)", done: false, tip: "LLC is the most popular for small businesses.", sources: [{ name: "LegalZoom", url: "https://www.legalzoom.com/business/business-formation/llc-overview.html" }, { name: "ZenBusiness", url: "https://www.zenbusiness.com/" }] },
    { id: "find-accountant", text: "Find an accountant", done: false, tip: "A good accountant saves you money.", sources: [{ name: "QuickBooks ProAdvisor", url: "https://quickbooks.intuit.com/find-an-accountant/" }, { name: "Bench", url: "https://bench.co/" }] },
    { id: "search-locations", text: "Search for potential locations", done: false, tip: "Visit at different times of day.", sources: [{ name: "LoopNet", url: "https://www.loopnet.com/" }, { name: "Crexi", url: "https://www.crexi.com/" }] },
    { id: "check-zoning", text: "Check zoning requirements for your location", done: false, tip: "Call before you sign a lease!", sources: [{ name: "SBA Zoning Guide", url: "https://www.sba.gov/business-guide/launch-your-business/pick-your-business-location" }] },
    { id: "request-permits", text: "Request permits and licenses information", done: false, tip: "Make a list and tackle them one at a time.", sources: [{ name: "SBA Permits Tool", url: "https://www.sba.gov/business-guide/launch-your-business/apply-for-licenses-and-permits" }] },
    { id: "build-financial-plan", text: "Build your financial plan", done: false, tip: "Include at least 6 months of operating expenses.", sources: [{ name: "SCORE Business Plan Template", url: "https://www.score.org/resource/business-plan-template-startup-business" }] },
    { id: "secure-lease", text: "Secure a lease for your space", done: false, tip: "Negotiate! Ask for free rent months and TI allowance.", sources: [{ name: "Nolo Lease Guide", url: "https://www.nolo.com/legal-encyclopedia/commercial-leases" }] },
    { id: "order-equipment", text: "Order kitchen and restaurant equipment", done: false, tip: "Consider used/refurbished to save 30–50%.", sources: [{ name: "WebstaurantStore", url: "https://www.webstaurantstore.com/" }] },
    { id: "hire-staff", text: "Hire your initial staff", done: false, tip: "Start with your most critical role.", sources: [{ name: "Indeed", url: "https://www.indeed.com/hire" }, { name: "Homebase", url: "https://joinhomebase.com/" }] },
    { id: "create-branding", text: "Create your branding and visual identity", done: false, tip: "Invest in a good logo and consistent style.", sources: [{ name: "Canva", url: "https://www.canva.com/" }, { name: "99designs", url: "https://99designs.com/" }] },
    { id: "launch-marketing", text: "Launch your marketing strategy", done: false, tip: "Start with free channels: Instagram, Google Business.", sources: [{ name: "Google Business Profile", url: "https://business.google.com/" }] },
    { id: "open-business", text: "Open your business!", done: false, tip: "Do a soft opening first.", sources: [{ name: "SBA Opening Day Guide", url: "https://www.sba.gov/business-guide" }] },
  ];

  const commonMistakes = [
    "Underestimating buildout costs — always add 20% buffer",
    "Skipping the permit research — this can delay you months",
    "Choosing a location based on rent alone — foot traffic matters more",
    "Not having 3–6 months of operating cash reserves",
    "Trying to do everything yourself instead of hiring help early",
  ];

  const first7Days = [
    "Day 1: Research your city's business registration requirements",
    "Day 2: Choose your business structure (LLC is most common for beginners)",
    "Day 3: Open a business bank account",
    "Day 4: Start looking at 5–10 potential locations online",
    "Day 5: Call your city's business licensing office",
    "Day 6: Talk to 3 people who own similar businesses",
    "Day 7: Create a simple 1-page business plan",
  ];

  const leaseListings = generateLeaseListings(data.city, budgetValue);
  const cityCenter = getCityCenter(data.city);
  const staffing = generateStaffing(data, budgetValue);
  // Scale per-row monthly labor by the city cost multiplier (Miami = 1.00).
  // Headcounts stay unchanged; only the dollar estimates are adjusted, so the
  // dashboard total and downstream consumers (PDF, forecast, etc.) all see
  // city-weighted wages without a separate codepath.
  const laborMult = getCityMultiplier(data.city);
  for (const row of staffing) {
    row.monthlyEstimate = Math.round(row.monthlyEstimate * laborMult);
  }
  const totalMonthlyLabor = staffing.reduce((s, r) => s + r.monthlyEstimate, 0);
  // Recommended sqft must be driven by INDOOR seat count (outdoor seats
  // don't need indoor floor area). Falls back to total seats if indoor
  // wasn't captured (older onboardings).
  const indoorSeatsForSqft = Math.max(
    0,
    Number(data.indoorSeats) || Number(data.seats) || 0,
  );
  const recommendedSqft = getRecommendedSqft(data.businessType, indoorSeatsForSqft, data.city);
  const milestones = generateMilestones(data);

  // Opening summary
  const serviceLabel = serviceStyles.find(s => s.id === data.serviceStyle)?.label || data.serviceStyle;
  const seatingLabel = seatingOptions.find(s => s.id === data.seatingType)?.label || data.seatingType;
  const indoorSeats = Math.max(0, Number(data.indoorSeats) || 0);
  const outdoorSeats = Math.max(0, Number(data.outdoorSeats) || 0);
  const barSeats = Math.max(0, Number(data.barSeats) || 0);
  const totalCapacity = indoorSeats + outdoorSeats + barSeats;
  const openingSummary = {
    businessType: businessLabel,
    city: data.city,
    targetDate: data.targetOpeningDate,
    sqft: formatAreaRange(data.sqftMin || recommendedSqft.min, data.sqftMax || recommendedSqft.max, data.city),
    seats: totalCapacity > 0 ? totalCapacity : (data.seats || 0),
    indoorSeats,
    outdoorSeats,
    barSeats,
    totalCapacity,
    seating: seatingLabel,
    serviceStyle: serviceLabel,
    hours: data.openingHours || "9 AM – 9 PM",
    daysPerWeek: data.daysPerWeek || 7,
    team: data.teamSize === "lean" ? "Lean team (you + 1–3 people)" : "Full team from day one",
    totalWeeks,
    // ── Business-type-specific capacity context for PDF / UI ──
    // Hotels & B&Bs ─ room counts + extras
    totalRooms: (data.guestRooms ?? []).reduce(
      (sum, r) => sum + (Number(r.quantity) || 0),
      0,
    ),
    suitesCount: Number(data.suitesCount) || 0,
    meetingRoomsCapacity: Number(data.meetingRoomsCapacity) || 0,
    // Private chef / catering ─ event-driven model
    guestsPerEvent: Number(data.guestsPerEvent) || 0,
    eventsPerMonth: Number(data.eventsPerMonth) || 0,
    avgMonthlyCovers:
      (Number(data.guestsPerEvent) || 0) * (Number(data.eventsPerMonth) || 0),
    kitchenLocation: (data.kitchenLocation || "") as string,
    // Catering ─ max event capacity
    maxCapacityPerEvent: Number(data.maxCapacityPerEvent) || 0,
    // Food truck ─ counter seats + daily covers
    counterSeats: Number(data.counterSeats) || 0,
    avgDailyCovers: Number(data.avgDailyCovers) || 0,
    // Original business-type id so the PDF can branch on it
    businessTypeId: data.businessType,
  };

  return {
    businessLabel,
    city: data.city,
    currencySymbol: currSymbol,
    isUK,
    timeline,
    neighborhoods,
    timelineSteps,
    permits,
    costBreakdown,
    totalEstimate,
    budgetFit,
    budgetLabel: localBudgetLabel,
    checklist,
    commonMistakes,
    first7Days,
    openingScore: data.experience === "experienced" ? 85 : data.experience === "some" ? 72 : 58,
    leaseListings,
    cityCenter,
    staffing,
    totalMonthlyLabor,
    recommendedSqft,
    milestones,
    openingSummary,
    // Investor-facing profile band for this business type. Lets UI surfaces
    // (Setup card, Permits, Budget) show the canonical Restaurant / Café /
    // Hotel… ranges alongside the user's specific numbers.
    typeProfile: typeProfile ?? null,
  };
}

export type MockResults = ReturnType<typeof generateMockResults>;
